import {
  Link, Typography
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Auth0Lock } from 'auth0-lock';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Alert as BsAlert } from 'reactstrap';
import logo from '../../images/blumira_login_blue.png';
import { auth, AUTH_STATUS } from '../../lib/auth';
import config from '../../lib/config';
import { storageAvailable } from '../../lib/helpers';
import Logger from '../../lib/logger';
import { scopeSetUser } from '../../lib/sentry';
import OutageBanner from '../Components/OutageBanner';
import SignUpView from './SignUpView';

const logger = Logger('Login View');

/*
Constanst and Helpers
*/

const STORAGE_TYPES_IN_USE = ['localStorage', 'sessionStorage'];

// Exracts the user ID from the Auth0 authResult
const getUserId = (authResult) => {
  const { idTokenPayload } = authResult;

  const { auth = {} } = config;
  const { jwt = {} } = auth;
  const { namespace } = jwt;

  let userId = 'Unknown';

  if (idTokenPayload && namespace) {
    userId = idTokenPayload[`${namespace}/person_id`]
      || 'No person_id in JWT';
  }

  return userId;
};

/*
Component
*/

const makeLock = (connection, container, query, displayName) => {
  return new Auth0Lock(
    config.auth.auth0.client_id,
    config.auth.auth0.domain,
    {
      avatar: null,
      allowedConnections: [connection],
      configurationBaseUrl: 'https://cdn.auth0.com',
      container: container,
      auth: {
        sso: false,
        responseType: 'id_token',
        audience: config.auth.auth0.audience,
        params: {
          scope: 'openid profile',
        },
      },
      initialScreen: query.supportForgotPassword ? 'forgotPassword' : 'login',
      prefill: {
        email: query.email,
      },
      autoclose: true,
      languageDictionary: {
        enterpriseLoginIntructions: '',
        loginWithLabel: 'Continue with %s',
        forgotPasswordTitle: 'Set or Reset your password',
        forgotPasswordAction: 'Set or Reset Password',
        forgotPasswordInstructions: 'Please enter your email address. ' +
          'We will send you an email to set or reset your password.',
      },
      theme: {
        primaryColor: '#3BB273',
        logo: 'https://blu-public-assets.storage.googleapis.com/images/blu-logo.png',
        authButtons: {
          "MS-SSO": {
            displayName: displayName,
            primaryColor: "#00a1f1",
            foregroundColor: "#FFFFFF",
          },
          "Username-Password-Authentication": {
            primaryColor: '#3BB273',
            foregroundColor: "#ffffff",
          }
        }
      },
      // Disable the 'grey box' prefilled login
      rememberLastLogin: false,
    }
  );
}

class LoginView extends Component {
  state = {
    error: null,
    isLoggingIn: false,
    headerMsg: 'Login to your account',
    firstTime: false,
    storageAlert: false, // Show alert if browser storage is unavailable
    cardShown: 'logIn'
  };

  componentDidMount() {
    logger.log('Login view mounted');
    const storageOK = STORAGE_TYPES_IN_USE.reduce(
      (acc, cur) => acc && storageAvailable(cur), true
    );
    this.setState({ storageAlert: !storageOK });
    this.onAuthenticated = this.onAuthenticated.bind(this);

    if (auth.currentStatus() === AUTH_STATUS.LOGGED_IN)
      return this.setState({ isLoggingIn: true })

    const { query = {} } = this.props;

    /*
      If the user is coming from the initial welcome email, then query.message
      will start with 'Your email was verified'.
      We will set a flag called 'firstTime' in the state so that if other UI
      customizations need to be made, they can refer to the flag.
    */
    if (query.message && query.message.startsWith('Your email was verified.')) {
      this.setState({ firstTime: true });
      this.setState({
        headerMsg:
          'Please complete account creation by using the form below. \nYour ' +
          'email must be the same as in the Blumira invite you just clicked.',
      });
    }
    this.lock = makeLock('Username-Password-Authentication', 'login-root', query)
    this.lock.on('authenticated', this.onAuthenticated);
    this.lock.on('authorization_error', this.onAuthError);
    this.lock.on('forgot_password submit', this.onForgotPasswordSubmit);

    this.ssoLock = makeLock('MS-SSO', 'sso-root', query, this.state.cardShown === 'login' ? "Sign up with Microsoft" : "Continue with Microsoft")
    this.ssoLock.on('authenticated', this.onAuthenticated);
    this.ssoLock.on('authorization_error', this.onAuthError);
    this.ssoLock.on('forgot_password submit', this.onForgotPasswordSubmit);

    if (query.logout === 'true') {
      // Logout from auth0
      this.lock.logout(
        {
          returnTo: window.location.origin,
        }
      );

      // Clear the logout='true' query
      this.props.dispatchClearLogoutQuery();

      // Unset the Sentry scope.user
      scopeSetUser(null);
    }

    this.lock.show();
    this.ssoLock.show();
  }

  onForgotPasswordSubmit = () => {
    if (this.state.firstTime) {
      this.setState({
        headerMsg: 'Please check your email'
          + ' to finish setting your initial Blumira password.',
      });
    } else {
      this.setState({
        headerMsg: 'Please check your email'
          + ' to complete the password reset process.',
      });
    }
    document.getElementById('login-root').style.display = 'none';
    document.getElementById('welcome').style.visibility = 'hidden';
  };

  onAuthError = (error) => {
    const message = 'Auth0 login error:';
    const errMsg = `${message} ${JSON.stringify(error)}`;

    logger.error(message, new Error(errMsg));

    this.lock.hide();
    this.lock.show({
      flashMessage: {
        type: 'error',
        text: error.errorDescription || error.description,
      },
    });
  };

  handleAuthenticatedUI = () => {
    // show user progress on logging in
    this.setState({
      isLoggingIn: true,
      headerMsg: 'Logging into your account...'
    })

    // completely hide auth0 component
    this.lock.hide()
  }

  handleCardChange = () => {
    this.setState({ cardShown: 'signUp', headerMsg: 'Sign up with email' })
  }

  onAuthenticated = (authResult) => {
    logger.log('User succesfully authenticated with Auth0');

    this.handleAuthenticatedUI()

    const userId = getUserId(authResult);
    scopeSetUser({ userId });

    let redirectPayload = null;
    try {
      redirectPayload = JSON.parse(sessionStorage.getItem('payload'));
      sessionStorage.removeItem('payload');
    } catch (err) {
      logger.error('Failed to read payload from Session Storage.', err);
    }

    // Login to Blumira
    this.props.dispatchLoginToBlumira({ authResult, redirectPayload });
  };

  render() {
    const { isLoggingIn } = this.state
    const error = this.state.error || this.props.error;
    let devDiagnostics = null;
    switch (config.deploymentType) {
      case 'local-dev':
      case 'development': {
        devDiagnostics = (
          <div>
            <p>{`build: ${config.image_tag}`}</p>
            <p>{`auth0_client_id: ${config.auth.auth0.client_id}`}</p>
            <p>{`auth0 domain: ${config.auth.auth0.domain}`}</p>
            <p>{`FEA URL: ${config.api}`}</p>
            <p
              style={{
                marginTop: 10,
                fontWeight: 400,
                fontSize: '16px',
                whiteSpace: 'pre-line',
              }}
            >
              Don't have an account?
            </p>
            <Typography variant="caption" align="center" style={{ opacity: '0.6' }}>
             Sign up with a Microsoft<br/> account above or<br/>
            </Typography>
            <Link component="button" variant="caption" style={{ cursor: "pointer", opacity: '1' }} onClick={this.handleCardChange}>Sign up with email</Link>
          </div>
        );
        break;
      }
      default:
        devDiagnostics = (<div><p>{`build: ${config.image_tag}`}</p> <p
          style={{
            marginTop: 10,
            fontWeight: 400,
            fontSize: '16px',
            whiteSpace: 'pre-line',
          }}
        >
          Don't have an account?
        </p>
          <Typography variant="caption" align="center" style={{ opacity: '0.6' }}>
          Sign up with a Microsoft<br/> account above or<br/>
          </Typography>
          <Link component="button" variant="caption" style={{ cursor: "pointer", opacity: '1' }} onClick={this.handleCardChange}>Sign up with email</Link>
        </div>);
    }

    return (
      <>
        <div className={`h-100 w-100 d-flex align-items-center ${this.state.cardShown === 'logIn' ? 'login' : ''}`} style={{ flexFlow: 'column' }}>

          <div
            className='w-100'
          >
            <div className='row mx-auto align-items-center'>
              <div
                style={{ textAlign: 'center' }}
                className='col align-items-center mx-auto'
              >
                <img
                  className='mx-auto'
                  style={{
                    width: '100%',
                    minWidth: '200px',
                    maxWidth: '300px',
                  }}
                  src={logo}
                  alt='Logo'
                  title='Blumira'
                />
                <p
                  style={{
                    marginTop: 10,
                    fontWeight: 400,
                    fontSize: '16px',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {this.state.headerMsg}
                </p>

                <OutageBanner />

                {this.state.storageAlert && (
                  <div>
                    <BsAlert color='info'>
                      <div>Your browser cookies and storage are disabled.</div>
                      <h2 className='alert-subtitle'>
                        Please enable browser cookies and storage
                      </h2>
                      <div className='alert-subtitle'>and refresh the page</div>
                    </BsAlert>
                  </div>
                )}

              </div>
            </div>
            {
              this.state.cardShown === 'logIn' &&
              <div>
                <form
                  className='mx-auto'
                  onSubmit={(e) => { e.preventDefault(); }}
                  style={{ paddingBottom: '40px', marginTop: -10 }}
                >
                  <div
                    className='col align-items-center'
                    style={{
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      padding: '0px',
                    }}
                  >
                    {error && error.message && (
                      <div className='alert alert-danger' role='alert'>
                        {error.message}
                      </div>
                    )}
                    {!!isLoggingIn && <CircularProgress />}
                    <div
                      id='sso-root'
                      style={{
                        display: this.state.storageAlert ? 'none' : 'block',
                      }}
                    />
                    <div className='form-group' style={{ marginTop: '10px' }}>
                      <div
                        id='login-root'
                        style={{
                          display: this.state.storageAlert ? 'none' : 'block',
                        }}
                      />
                    </div>
                    <div style={{
                      fontSize: '10px',
                      marginTop: '20px',
                    }}
                    >
                      {devDiagnostics && (
                        <div className='dev-diagnostics'>
                          {devDiagnostics}
                        </div>
                      )}
                    </div>
                  </div>
                </form>

              </div>
            }
          </div>

          {
            this.state.cardShown === 'signUp' &&
            <SignUpView />
          }


        </div>

      </>
    );
  }
}

LoginView.propTypes = {
  query: PropTypes.shape({
    logout: PropTypes.string,
    message: PropTypes.string,
  }),
  error: PropTypes.shape({ message: PropTypes.string }),
  dispatchClearLogoutQuery: PropTypes.func.isRequired,
  dispatchLoginToBlumira: PropTypes.func.isRequired,
};

LoginView.defaultProps = {
  query: {
    logout: 'false',
  },
  error: null,
};

export default LoginView;
